import React, { useRef } from 'react';
import ringArt from '../../assets/img/portal-ring.png';
import { RING } from '../wall/wallTokens';

/**
 * The Portal ring — a swirl that spins around someone's photo while they have a
 * Portal or a story up. Design handoff: docs/plans/2026-09-portals-stories/handoff/README.md §1,
 * and the city tab from design_handoff_portal_landing_rail_pro (Portals Rail City, option 1b).
 *
 * The ring is the one place the design system's square-avatar rule is broken, and only while
 * something is live: the photo is the usual 2px-bordered square everywhere else, and becomes a
 * circle inside the ring for as long as there's something to watch. Nothing else in the product
 * is round.
 *
 * Geometry is proportional so one component serves every placement (rail 64/72, rows 56,
 * navbar 46, the Resume photo at inset -7%): the art's band runs 0.875r→1.00r, so the face sits
 * at a 5.5% inset with no gap and no collar between it and the band. Only the swirl layer
 * rotates — the face never does.
 *
 * Props:
 *   state  'unseen' | 'live' | 'seen' | 'text' | 'club' | 'send' | 'square' | 'intro'
 *          'intro' is an introduction portal: the moving iridescent ring instead of the swirl,
 *          `tag` (YOU / NEW) on its foot and `sub` (the days left) under the name.
 *          'square' is the City Portal door: a cyan radar face — a sweep, two faint rings, a
 *          beacon and three blips — and a cyan "N IN" tag instead of a countdown. Cyan is the
 *          signal species colour.
 *   count  'square' only: people in the local square or posting to it; 0 hides the tag
 *   size   ring box in px
 *   countdown  the LIVE pill's text, e.g. "3H 41M"
 *   snippet    first words of a text-only Portal (state='text')
 *   city       the town the Portal is hosted in — a smoked-glass tab notched into the top of
 *              the ring (option 1b): the ring colours show through it, and a page-coloured
 *              gap (`pageBg`) cuts it from the swirl. A Portal sent to the City Portal wears
 *              it in cyan (`cityTone="square"`); a watched one goes grey on its own.
 */
export const CITY_TAB = {
  default: { bg: 'rgba(18,14,28,.55)', fg: '#FFFFFF' },
  square: { bg: 'rgba(0,40,48,.55)', fg: '#00FFFF' },
  seen: { bg: 'rgba(120,120,128,.55)', fg: '#FFFFFF' },
};

export default function PortalRing({
  state = 'unseen',
  size = 64,
  avatarUrl = null,
  fallbackTint = null,
  initial = '',
  countdown = null,
  count = 0,
  snippet = '',
  label = null,
  /** 'intro' only: the tag on the ring's foot and the line under the name. */
  tag = null,
  sub = null,
  city = null,
  cityTone = null,
  /** The rail's own background — the colour of the gap between the city tab and the ring. */
  pageBg = '#fff',
  /** `{ x, y }` percentages — where in the photo the circle should look (CoverFocusEditor). */
  focus = null,
  onClick = null,
  ariaLabel = null,
  className = '',
}) {
  const pressedRect = useRef(null);
  const isSend = state === 'send';
  const isSeen = state === 'seen';
  const isTextOnly = state === 'text';
  const isIntro = state === 'intro';
  const spins = !isSend && !isSeen && !isIntro;
  // The handoff draws two sizes: the rail (72) and a 160px detail. Everything in between
  // takes the small numbers; anything 120 and up, the large ones.
  const big = size >= 120;

  const faceInset = isIntro ? '5px' : `${(size * 0.055).toFixed(2)}px`;
  const Tag = onClick ? 'button' : 'div';

  const tagText = state === 'square' ? (count > 0 ? `${count} IN` : null) : ((state === 'live' || isTextOnly) && countdown ? countdown : null);
  const tagStyle = {
    position: 'absolute', left: '50%', bottom: -7, transform: 'translateX(-50%)', zIndex: 2, whiteSpace: 'nowrap',
    background: state === 'square' ? '#00FFFF' : '#FF00AA', color: state === 'square' ? '#000' : '#fff',
    fontSize: big ? 12 : 8, fontWeight: 800, letterSpacing: '0.1em', padding: big ? '4px 10px' : '2px 7px', borderRadius: 999,
    boxShadow: `0 0 0 2px ${pageBg}`,
  };

  const tone = CITY_TAB[cityTone || (isSeen ? 'seen' : 'default')] || CITY_TAB.default;
  const cityTab = city && !isSend && state !== 'square' ? (
    <span
      data-testid="ring-city"
      style={{
        position: 'absolute', left: '50%', top: -8, transform: 'translateX(-50%)', zIndex: 3,
        display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
        padding: big ? '4px 10px' : '2px 7px', borderRadius: 999,
        background: tone.bg, color: tone.fg,
        fontSize: big ? 12 : 8, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
        boxShadow: `0 0 0 ${big ? 4 : 2.5}px ${pageBg}`,
        backdropFilter: 'blur(6px) saturate(1.6)', WebkitBackdropFilter: 'blur(6px) saturate(1.6)',
      }}
    >
      <svg width={big ? 12 : 8} height={big ? 12 : 8} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square" aria-hidden>
        <path d="M12 22s7-7.2 7-12.5A7 7 0 0 0 5 9.5C5 14.8 12 22 12 22z" />
        <circle cx="12" cy="9.5" r="2.2" fill="currentColor" stroke="none" />
      </svg>
      {city}
    </span>
  ) : null;

  const face = (() => {
    if (state === 'square') {
      const dot = Math.max(4, Math.round(size / 12));
      const blip = Math.max(2, Math.round(size / 24));
      return (
        <span
          className="absolute overflow-hidden rounded-full"
          style={{ inset: faceInset, background: 'radial-gradient(circle at 50% 50%, #062A30 0%, #000 68%)' }}
          aria-hidden
          data-testid="square-face"
        >
          <span className="pr-radar-sweep absolute inset-0 rounded-full" style={{ background: 'conic-gradient(from 0deg, rgba(0,255,255,0) 0deg, rgba(0,255,255,0) 280deg, rgba(0,255,255,.55) 352deg, rgba(0,255,255,.9) 360deg)' }} />
          <span className="absolute rounded-full" style={{ inset: '18%', boxShadow: 'inset 0 0 0 1px rgba(0,255,255,.28)' }} />
          <span className="absolute rounded-full" style={{ inset: '34%', boxShadow: 'inset 0 0 0 1px rgba(0,255,255,.4)' }} />
          <span className="pr-radar-beacon absolute rounded-full" style={{ left: '50%', top: '50%', width: dot, height: dot, margin: `${-dot / 2}px 0 0 ${-dot / 2}px`, background: '#00FFFF' }} />
          <span className="absolute rounded-full" style={{ left: '28%', top: '30%', width: blip, height: blip, background: '#FF00AA' }} />
          <span className="absolute rounded-full" style={{ left: '64%', top: '58%', width: blip, height: blip, background: '#fff' }} />
          <span className="absolute rounded-full" style={{ left: '40%', top: '70%', width: blip, height: blip, background: '#00FFFF' }} />
        </span>
      );
    }
    if (isSend) {
      return (
        <span
          className="absolute inset-[2px] flex items-center justify-center rounded-full border-2 border-black bg-white text-black"
          aria-hidden
        >
          <svg width={big ? 34 : 22} height={big ? 34 : 22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
            <path d="M5 12h14M12 5v14" />
          </svg>
        </span>
      );
    }
    if (isTextOnly) {
      return (
        <span
          className="absolute overflow-hidden rounded-full bg-black px-1.5 text-center text-white"
          style={{ inset: faceInset, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          aria-hidden
        >
          <span
            className="line-clamp-3"
            style={{ fontFamily: "'Times New Roman', Times, serif", fontStyle: 'italic', fontSize: Math.max(10, Math.round(size * 0.17)), lineHeight: 1.15 }}
          >
            {snippet}
          </span>
        </span>
      );
    }
    return (
      <span
        className="absolute overflow-hidden rounded-full"
        style={{ inset: faceInset, background: (!avatarUrl && fallbackTint) || (isSeen ? '#E5E7EB' : '#1A1A1A') }}
        aria-hidden
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-full w-full"
            style={{ objectFit: 'cover', objectPosition: focus ? `${focus.x}% ${focus.y}%` : '50% 50%' }}
          />
        ) : (
          <span
            className="flex h-full w-full items-center justify-center font-mono font-bold"
            style={{ color: fallbackTint ? '#000000' : isSeen ? '#6B7280' : '#FFFFFF', fontSize: Math.round(size * 0.34) }}
          >
            {(initial || '?').slice(0, 2).toUpperCase()}
          </span>
        )}
      </span>
    );
  })();

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <Tag
        {...(onClick ? {
          type: 'button',
          onPointerDown: e => { const r = e.currentTarget.getBoundingClientRect(); pressedRect.current = { left: r.left, top: r.top, width: r.width, height: r.height }; },
          onPointerCancel: () => { pressedRect.current = null; },
          onClick: e => { e.portalRingRect = e.detail === 0 ? null : pressedRect.current; pressedRect.current = null; onClick(e); },
        } : {})}
        aria-label={ariaLabel || label || undefined}
        // The ring is a circle, so the focus ring has to be one too — and a mouse click should
        // not leave a square outline behind it (focus-visible, not focus).
        className={`relative block flex-shrink-0 rounded-full ${onClick ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2' : ''}`}
        style={{ width: size, height: size }}
      >
        {/* The swirl. A background-image rather than an <img> so a missing asset makes no
            request and leaves the face alone. */}
        {spins && (
          <span
            className="pr-portal-ring absolute inset-0"
            style={{ backgroundImage: `url(${ringArt})` }}
            aria-hidden
          />
        )}
        {/* An introduction wears the moving iridescent ring, with a gap to the page. */}
        {isIntro && (
          <span
            className="pr-ring-anim absolute inset-0 rounded-full"
            style={{ border: '3px solid transparent', background: `linear-gradient(${pageBg},${pageBg}) padding-box, ${RING} border-box` }}
            aria-hidden
            data-testid="ring-intro"
          />
        )}
        {/* Watched: the swirl stops being the point, so it goes entirely. */}
        {isSeen && (
          <span className="absolute inset-[2px] rounded-full border-2" style={{ borderColor: '#9CA3AF' }} aria-hidden />
        )}

        {face}

        {/* A room, not a person. */}
        {state === 'club' && (
          <span
            className="absolute border-2 border-white bg-black"
            style={{ width: 14, height: 14, top: -2, right: -2 }}
            aria-hidden
          />
        )}

        {/* Where it's happening — notched into the top of the ring. */}
        {cityTab}

        {/* The City Portal's headcount (cyan, the signal colour), or how long is left — the
            only place magenta appears on the rail. A text Portal is as live as a photo one, so
            it wears the same tag. */}
        {isIntro && tag && (
          <span
            data-testid="ring-tag"
            className="absolute flex items-center whitespace-nowrap font-mono font-extrabold"
            style={{ left: '50%', bottom: -6, transform: 'translateX(-50%)', height: 18, padding: '0 8px', borderRadius: 999, background: tag === 'YOU' ? '#fff' : '#22ECFB', color: '#000', fontSize: 8, letterSpacing: '.14em', boxShadow: `0 0 0 2px ${pageBg}` }}
          >
            {tag}
          </span>
        )}
        {tagText && (
          <span
            className="font-mono uppercase"
            style={tagStyle}
            data-testid={state === 'square' ? 'square-count' : 'ring-countdown'}
          >
            {tagText}
          </span>
        )}
      </Tag>

      {label && (
        <span
          className="block max-w-full truncate font-mono font-bold uppercase"
          style={{
            // A tag hangs 7px under the ring; the label clears it.
            marginTop: tagText || (isIntro && tag) ? (big ? 16 : 10) : 6,
            fontSize: big ? 13 : size >= 72 ? 10 : 9, letterSpacing: '0.05em', color: isSeen ? '#9CA3AF' : '#000000', maxWidth: size + 8,
          }}
        >
          {label}
        </span>
      )}
      {isIntro && sub && (
        <span className="mt-0.5 block whitespace-nowrap font-mono font-bold uppercase" style={{ fontSize: 8, letterSpacing: '.12em', color: '#6B7280' }}>{sub}</span>
      )}
    </div>
  );
}
